import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * Independent Stays plan IS6: add or drop a product from Billing
 * (BE src/products/routes.js). Every amount is integer cents from the server;
 * nothing here computes a price. Refusals carry the server's plain message.
 */

export type ProductChangeKind = 'add_desk' | 'add_stays' | 'drop_desk' | 'drop_stays';

export interface ProductRefusal {
  message: string;
  code: string;
  count?: number;
}

export interface ProductsState {
  enabled: boolean;
  products: { desk: boolean; stays: boolean };
  account: string;
  billing: 'comped' | 'trialing' | 'active' | 'past_due' | 'none' | string;
  currency: string;
  can: Record<ProductChangeKind, boolean>;
  reason: Record<ProductChangeKind, ProductRefusal | null>;
  blockers: {
    drop_desk: never[];
    drop_stays: { code: string; count: number; link: string }[];
  };
  consequences: {
    drop_desk: {
      concierges: number;
      concierges_keep_reception: boolean;
      residents: number;
      live_passes: number;
      open_shifts: number;
      units_hidden: number;
      listings_kept: number;
    };
    drop_stays: { listings_retired: number };
  };
  options: {
    desk_plans: { code: string; name: string; unit_cents: number }[];
    stays_plans: { code: string; name: string; unit_cents: number }[];
    checkin_cents: number | null;
    addon_cents: number | null;
    slots_in_use: number;
  };
  front_desk_checklist: boolean;
}

export interface ProductQuote {
  request_id: string;
  kind: ProductChangeKind;
  lines: { label: string; detail: string; cents: number | null }[];
  today_cents: number;
  credit_cents: number;
  next_invoice_cents: number;
  next_invoice_at: string | null;
  currency: string;
  discounts_removed: unknown[];
  expires_at: string | null;
}

export interface ProductQuoteInput {
  kind: ProductChangeKind;
  plan_code?: string;
  stays_plan_code?: string;
  listing_slots?: number;
}

export class ProductsApiError extends Error {
  code: string;
  status: number;
  quote: ProductQuote | null;

  constructor(message: string, code: string, status: number, quote: ProductQuote | null) {
    super(message);
    this.code = code;
    this.status = status;
    this.quote = quote;
  }
}

const rethrow = (e: unknown): never => {
  const res = (e as AxiosError<{ message?: string; code?: string; quote?: ProductQuote }>).response;
  throw new ProductsApiError(
    (res && res.data && res.data.message) || 'That did not go through. Nothing was changed. Please try again.',
    (res && res.data && res.data.code) || 'UNKNOWN',
    (res && res.status) || 0,
    (res && res.data && res.data.quote) || null
  );
};

export const getProducts = (): Promise<ProductsState> =>
  axios
    .get<ProductsState>(`${apiRoute}/products`, { withCredentials: true })
    .then(r => r.data)
    .catch(rethrow);

export const quoteProductChange = (input: ProductQuoteInput): Promise<ProductQuote> =>
  axios
    .post<ProductQuote>(`${apiRoute}/products/quote`, input, { withCredentials: true })
    .then(r => r.data)
    .catch(rethrow);

export const applyProductChange = (requestId: string, expectedTodayCents: number): Promise<ProductsState> =>
  axios
    .post<ProductsState>(
      `${apiRoute}/products/apply`,
      { request_id: requestId, expected_today_cents: expectedTodayCents },
      { withCredentials: true }
    )
    .then(r => r.data)
    .catch(rethrow);
